import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useFeaturesQuery } from '@/features/features';
import { useDetailFeatureQuery } from '@/features/features/hooks/useDetailFeatureQuery';
import { entityFormAdapters } from '@/features/forms';
import { useReleasesQuery, useUpdateRelease } from '@/features/releases';
import { queryRelatedReleases } from '@/features/releases/data';
import { useCreateTest, useTestsQuery, useUpdateTest } from '@/features/tests';
import { gridPaths } from '@/shared/hooks';
import { useInvalidateGrids } from '@/shared/hooks/useInvalidateGrids';
import { useProjectContextGuard } from '@/shared/hooks/useProjectContextGuard';
import { hashString } from '@/shared/utils/hash';
import { buildDetailGridQueryKey } from '@/ui/composites/grid/queryKeyUtils';
import { emptyQueryResult } from '@/ui/composites/grid/queryUtils';
import {
  useDialogFormState,
  type DialogFormStateWithHandlers,
} from '@/ui/hooks/useDialogFormState';
import { useFormErrors, type FieldErrorProps } from '@/ui/hooks/useFormErrors';
import { useLinkSelectDialogState } from '@/ui/hooks/useLinkSelectDialogState';
import { useSuiteDetailQuery, useSuitesQuery, useUpdateSuite, useDeleteSuite } from './suites';
import type { SuiteFormValues, TestFormValues } from '@/features/forms';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type {
  FeatureDto,
  QueryResult,
  ReleaseListItemDto,
  SuiteListItemDto,
  TestListItemDto,
} from '@flowqa/shared';

type SuiteDetailViewModelParams = {
  slug: string | undefined;
  selectedOrganisationId: string | null;
  selectedProjectId: string | null;
  setSelectedProjectId: (value: string | null) => void;
};

type SuiteDetailViewModel = {
  searchSlug: string;
  suiteQuery: ReturnType<typeof useSuiteDetailQuery>;
  suite: ReturnType<typeof useSuiteDetailQuery>['data'];
  featuresQuery: ReturnType<typeof useFeaturesQuery>;
  featureOptions: FeatureDto[];
  testsQuery: ReturnType<typeof useTestsQuery>;
  testOptions: TestListItemDto[];
  suitesQuery: ReturnType<typeof useSuitesQuery>;
  suiteOptions: SuiteListItemDto[];
  releasesQuery: ReturnType<typeof useReleasesQuery>;
  releaseOptions: ReleaseListItemDto[];
  editForm: DialogFormStateWithHandlers<SuiteFormValues>;
  editErrors: {
    errorFor: (key: keyof SuiteFormValues) => string | undefined;
    fieldErrorProps: (key: keyof SuiteFormValues) => FieldErrorProps;
    clearError: (key: keyof SuiteFormValues) => void;
  };
  testForm: DialogFormStateWithHandlers<TestFormValues>;
  testErrors: {
    errorFor: (key: keyof TestFormValues) => string | undefined;
    fieldErrorProps: (key: keyof TestFormValues) => FieldErrorProps;
    clearError: (key: keyof TestFormValues) => void;
  };
  linkTestDialog: ReturnType<typeof useLinkSelectDialogState>;
  unlinkTestDialog: ReturnType<typeof useLinkSelectDialogState>;
  linkTestForm: ReturnType<typeof useLinkSelectDialogState>['form'];
  linkTestErrors: ReturnType<typeof useLinkSelectDialogState>['errors'];
  unlinkTestForm: ReturnType<typeof useLinkSelectDialogState>['form'];
  unlinkTestErrors: ReturnType<typeof useLinkSelectDialogState>['errors'];
  editOpen: boolean;
  setEditOpen: (open: boolean) => void;
  deleteOpen: boolean;
  setDeleteOpen: (open: boolean) => void;
  addTestOpen: boolean;
  setAddTestOpen: (open: boolean) => void;
  releaseRows: ReleaseListItemDto[];
  setReleaseRows: Dispatch<SetStateAction<ReleaseListItemDto[]>>;
  localFeatureIds: string[];
  setLocalFeatureIds: Dispatch<SetStateAction<string[]>>;
  featureFilterIds: string[];
  featureQueryKey: readonly unknown[];
  featureQueryFn: ReturnType<typeof useDetailFeatureQuery>['featureQueryFn'];
  availableFeatureOptions: FeatureDto[];
  linkedFeatureOptions: FeatureDto[];
  directReleaseOptions: ReleaseListItemDto[];
  availableReleaseOptions: ReleaseListItemDto[];
  releaseLookup: Map<string, ReleaseListItemDto>;
  releaseQueryKey: readonly unknown[];
  releaseQueryFn: (input: GridQueryInput) => Promise<QueryResult<ReleaseListItemDto>>;
  availableTestOptions: TestListItemDto[];
  unlinkTestOptions: TestListItemDto[];
  resolveSuiteIdsForTest: (testId: string) => string[];
  updateSuite: ReturnType<typeof useUpdateSuite>;
  deleteSuite: ReturnType<typeof useDeleteSuite>;
  createTest: ReturnType<typeof useCreateTest>;
  updateTest: ReturnType<typeof useUpdateTest>;
  updateRelease: ReturnType<typeof useUpdateRelease>;
  invalidateGrids: ReturnType<typeof useInvalidateGrids>;
};

export const useSuiteDetailViewModel = ({
  slug,
  selectedOrganisationId,
  selectedProjectId,
  setSelectedProjectId,
}: SuiteDetailViewModelParams): SuiteDetailViewModel => {
  const searchSlug = useMemo(() => (slug ?? '').trim(), [slug]);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [addTestOpen, setAddTestOpen] = useState(false);
  const [releaseRows, setReleaseRows] = useState<ReleaseListItemDto[]>([]);
  const [localFeatureIds, setLocalFeatureIds] = useState<string[]>([]);
  const updateSuite = useUpdateSuite();
  const deleteSuite = useDeleteSuite();
  const createTest = useCreateTest();
  const updateTest = useUpdateTest();
  const updateRelease = useUpdateRelease();
  const invalidateGrids = useInvalidateGrids();
  const linkTestDialog = useLinkSelectDialogState();
  const unlinkTestDialog = useLinkSelectDialogState();

  const suiteQuery = useSuiteDetailQuery(searchSlug || null, selectedProjectId ?? null);
  const { data: suite } = suiteQuery;
  const featuresQuery = useFeaturesQuery(suite?.projectId ?? null);
  const featureOptions = featuresQuery.data?.items ?? [];
  const testsQuery = useTestsQuery(suite?.projectId ?? null, {
    enabled: Boolean(suite?.projectId),
  });
  const testOptions = testsQuery.data?.items ?? [];
  const suitesQuery = useSuitesQuery(suite?.projectId ?? null, {
    enabled: Boolean(suite?.projectId),
  });
  const suiteOptions = suitesQuery.data?.items ?? [];
  const releasesQuery = useReleasesQuery(suite?.projectId ?? null, {
    enabled: Boolean(suite?.projectId),
  });
  const releaseOptions = releasesQuery.data?.items ?? [];

  useProjectContextGuard({
    entity: suite,
    isPending: suiteQuery.isPending,
    isFetched: suiteQuery.isFetched,
    selectedProjectId,
    setSelectedProjectId,
    syncContextOnMismatch: true,
    listPath: '/suites',
  });

  const editForm = useDialogFormState<SuiteFormValues>(
    entityFormAdapters.suite.toFormValues(suite),
  );
  const editErrors = useFormErrors(editForm);
  const testForm = useDialogFormState<TestFormValues>({
    name: '',
    projectId: suite?.projectId ?? '',
    suiteIds: [],
  });
  const testErrors = useFormErrors(testForm);
  const linkTestForm = linkTestDialog.form;
  const linkTestErrors = linkTestDialog.errors;
  const unlinkTestForm = unlinkTestDialog.form;
  const unlinkTestErrors = unlinkTestDialog.errors;

  useEffect(() => {
    if (!suite?.projectId) return;
    testForm.setField('projectId', suite.projectId);
  }, [suite?.projectId, testForm.setField]);
  const { setField: setEditField } = editForm;
  const suiteFeatureIds = useMemo(() => {
    const features = suite?.features?.ids;
    return Array.isArray(features) ? features : [];
  }, [suite?.features?.ids, suite?.id]);

  useEffect(() => {
    if (!suite) return;
    setLocalFeatureIds(suiteFeatureIds);
    const nextValues = entityFormAdapters.suite.toFormValues(suite);
    setEditField('name', nextValues.name);
  }, [setEditField, suite?.id, suite?.name, suiteFeatureIds]);

  const featureIds = suite?.features?.ids ?? [];
  const featureFilterIds = localFeatureIds.length > 0 ? localFeatureIds : featureIds;
  const { featureQueryKey, featureQueryFn, normalizedFeatureIds } = useDetailFeatureQuery({
    entityId: suite?.id ?? null,
    tableKey: 'suite-feature-table',
    featureIds: featureFilterIds,
    projectId: suite?.projectId ?? null,
    organisationId: selectedOrganisationId ?? null,
  });
  const featureFilterIdSet = useMemo(() => new Set(normalizedFeatureIds), [normalizedFeatureIds]);
  const suiteId = suite ? suite.id : null;
  const availableFeatureOptions = useMemo(
    () => featureOptions.filter((feature) => !featureFilterIdSet.has(feature.id)),
    [featureFilterIdSet, featureOptions],
  );
  const linkedFeatureOptions = useMemo(
    () => featureOptions.filter((feature) => featureFilterIdSet.has(feature.id)),
    [featureFilterIdSet, featureOptions],
  );
  const directReleaseOptions = useMemo(
    () =>
      (releaseRows.length > 0 ? releaseRows : releaseOptions).filter((release) => {
        const suiteIds = release.suites?.ids;
        return Array.isArray(suiteIds) && suiteIds.includes(suiteId ?? '');
      }),
    [releaseOptions, releaseRows, suiteId],
  );
  const releaseLookup = useMemo<Map<string, ReleaseListItemDto>>(() => {
    const entries = [...releaseOptions, ...releaseRows].map(
      (release): [string, ReleaseListItemDto] => [release.id, release],
    );
    return new Map<string, ReleaseListItemDto>(entries);
  }, [releaseOptions, releaseRows]);
  const availableReleaseOptions = useMemo(
    () =>
      releaseOptions.filter(
        (release) => !directReleaseOptions.some((item) => item.id === release.id),
      ),
    [directReleaseOptions, releaseOptions],
  );
  const relatedReleaseIds = useMemo(
    () => ({
      suiteIds: suiteId ? [suiteId] : [],
      featureIds: Array.from(new Set(suiteFeatureIds)).sort(),
      testIds: [],
    }),
    [suiteFeatureIds, suiteId],
  );
  const relatedReleaseKey = useMemo(() => {
    const payload = JSON.stringify(relatedReleaseIds);
    const counts = [
      relatedReleaseIds.featureIds.length,
      relatedReleaseIds.suiteIds.length,
      relatedReleaseIds.testIds.length,
    ].join('-');
    return `${counts}-${hashString(`release-related:${payload}`)}`;
  }, [relatedReleaseIds]);
  const releaseQueryKey = buildDetailGridQueryKey(
    gridPaths.releasesRelated,
    'suite-release-table',
    suiteId ?? null,
    relatedReleaseKey,
    selectedOrganisationId ?? null,
  );
  const releaseQueryFn = useMemo(() => {
    if (!suite) {
      return async (input: GridQueryInput): Promise<QueryResult<ReleaseListItemDto>> =>
        Promise.resolve(emptyQueryResult(input));
    }
    return async (input: GridQueryInput): Promise<QueryResult<ReleaseListItemDto>> => {
      return (await queryRelatedReleases(
        suite.projectId,
        selectedOrganisationId ?? undefined,
        relatedReleaseIds,
        {
          ...input,
          include: {
            suites: { ids: true },
            features: { items: true },
            latestSuiteResults: { counts: true },
            latestTestResults: { counts: true },
          },
        },
      )) as QueryResult<ReleaseListItemDto>;
    };
  }, [relatedReleaseIds, selectedOrganisationId, suite]);
  const availableTestOptions = useMemo(() => {
    if (!suiteId) return [];
    return testOptions.filter((test) => {
      const suiteIds = test.suites?.ids ?? [];
      const primaryId = test.suites?.primaryId ?? null;
      if (primaryId === suiteId) return false;
      return !suiteIds.includes(suiteId);
    });
  }, [suiteId, testOptions]);
  const unlinkTestOptions = useMemo(() => {
    if (!suiteId) return [];
    return testOptions.filter((test) => {
      const suiteIds = test.suites?.ids ?? [];
      const primaryId = test.suites?.primaryId ?? null;
      if (primaryId === suiteId) {
        return true;
      }
      return suiteIds.includes(suiteId);
    });
  }, [suiteId, testOptions]);
  const resolveSuiteIdsForTest = useCallback(
    (testId: string): string[] => {
      const current = testOptions.find((test) => test.id === testId);
      if (!current) return [];
      const suiteIds = Array.isArray(current.suites?.ids) ? [...current.suites.ids] : [];
      const primaryId = current.suites?.primaryId ?? null;
      if (primaryId && !suiteIds.includes(primaryId)) {
        suiteIds.push(primaryId);
      }
      return suiteIds;
    },
    [testOptions],
  );

  return {
    searchSlug,
    suiteQuery,
    suite,
    featuresQuery,
    featureOptions,
    testsQuery,
    testOptions,
    suitesQuery,
    suiteOptions,
    releasesQuery,
    releaseOptions,
    editForm,
    editErrors,
    testForm,
    testErrors,
    linkTestDialog,
    unlinkTestDialog,
    linkTestForm,
    linkTestErrors,
    unlinkTestForm,
    unlinkTestErrors,
    editOpen,
    setEditOpen,
    deleteOpen,
    setDeleteOpen,
    addTestOpen,
    setAddTestOpen,
    releaseRows,
    setReleaseRows,
    localFeatureIds,
    setLocalFeatureIds,
    featureFilterIds,
    featureQueryKey,
    featureQueryFn,
    availableFeatureOptions,
    linkedFeatureOptions,
    directReleaseOptions,
    availableReleaseOptions,
    releaseLookup,
    releaseQueryKey,
    releaseQueryFn,
    availableTestOptions,
    unlinkTestOptions,
    resolveSuiteIdsForTest,
    updateSuite,
    deleteSuite,
    createTest,
    updateTest,
    updateRelease,
    invalidateGrids,
  };
};
